// @ts-check
import AppConfig from '../appConfig';

const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Download an XLSX export from the Rails backend.
 *
 * @param {string} endpoint  e.g. '/exports/commercial-developments'
 * @param {Record<string, string | number | undefined>} [params]
 * @param {{ method?: 'GET' | 'POST' }} [options]
 * @returns {Promise<Blob | {error: true, status: number, message: string} | null>}
 */
export async function downloadServerExport(endpoint, params = {}, options = {}) {
    const method = options.method || 'POST';

    try {
        let url;
        /** @type {RequestInit} */
        const fetchOptions = {
            method,
            credentials: 'include',
            headers: { Accept: XLSX_CONTENT_TYPE },
        };

        if (method === 'GET') {
            url = new URL(`${AppConfig.constants.backends.sewebsite}${endpoint}`);
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    url.searchParams.set(key, String(value));
                }
            });
            url = url.toString();
        } else {
            url = `${AppConfig.constants.backends.sewebsite}${endpoint}`;
            fetchOptions.headers['Content-Type'] = 'application/json';
            fetchOptions.body = JSON.stringify(params);
        }

        const response = await fetch(url, fetchOptions);

        if (!response.ok) {
            if (response.status === 403) {
                try {
                    const errorData = await response.json();
                    const message =
                        errorData && errorData.error ? errorData.error : 'Export limit reached';
                    console.warn('Export limit reached:', message);
                    return { error: true, status: 403, message };
                } catch (_) {
                    return { error: true, status: 403, message: 'Export limit reached' };
                }
            }
            if (response.status === 401) {
                console.warn('Export unauthorized — feature not available');
                return {
                    error: true,
                    status: 401,
                    message: 'Feature not available for your subscription',
                };
            }
            console.error('Server export failed with status:', response.status);
            return {
                error: true,
                status: response.status,
                message: 'Export failed. Please try again.',
            };
        }

        return await response.blob();
    } catch (err) {
        console.error('Server export failed:', err);
        return {
            error: true,
            status: 0,
            message: 'Network error. Please check your connection and try again.',
        };
    }
}

/**
 * Trigger a browser download from a Blob.
 *
 * @param {Blob} blob
 * @param {string} fileName
 */
export function triggerBlobDownload(blob, fileName) {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
}
